import React, { useEffect, useState } from 'react';

import { Select } from '../base/Select';
import { LoadingWheel } from '../base/LoadingWheel';

import useSuperAdminDataPoints from '../../data/datapoint/superadmin/useSuperAdminDataPoints';
import useSuperAdminDataPointProfiles from '../../data/datapoint/superadmin/useSuperAdminDataPointProfiles';

import { IDataPoint } from '../../types/DataPoint/DataPoint';

export interface DataPointSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'color'> {
  type: string;
  model: string;
  selected?: string;
  filterForActionProfiles?: boolean;
  filterForDataPointTransformations?: boolean;
  filterUnused?: boolean;
  filterUsed?: boolean;
  label?: string;
}

export const DataPointSelect = React.forwardRef<Omit<HTMLSelectElement, 'color'>, DataPointSelectProps>(
  (
    {
      type,
      model,
      selected,
      filterForActionProfiles,
      filterForDataPointTransformations,
      filterUnused,
      filterUsed,
      label = 'Data Point',
      ...rest
    },
    ref
  ) => {
    const { dataPoints: allDataPoints } = useSuperAdminDataPoints(type);
    const { dataPointProfiles } = useSuperAdminDataPointProfiles(model);
    const [dataPoints, setDataPoints] = useState<IDataPoint[] | undefined>(
      undefined
    );
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (
        !allDataPoints.isLoading &&
        !dataPointProfiles.isLoading &&
        allDataPoints.data &&
        dataPointProfiles.data
      ) {
        let currentDataPoint;
        let filteredDataPoints;

        if (filterForActionProfiles || filterForDataPointTransformations) {
          // Filter where datapoint profile exists
          filteredDataPoints = allDataPoints.data.filter((dataPoint) =>
            dataPointProfiles.data?.some(
              (decodeProfile) => decodeProfile.dataPointId === dataPoint.id
            )
          );

          if (filterForActionProfiles) {
            filteredDataPoints = allDataPoints.data.filter((dataPoint) =>
              dataPointProfiles.data?.some(
                (decodeProfile) => !decodeProfile.readOnly
              )
            );
          }
        } else if (filterUnused) {
          filteredDataPoints = allDataPoints.data.filter(
            (dataPoint) =>
              !dataPointProfiles.data?.some(
                (decodeProfile) => decodeProfile.dataPointId === dataPoint.id
              )
          );
        } else if (filterUsed) {
          filteredDataPoints = allDataPoints.data.filter((dataPoint) =>
            dataPointProfiles.data?.some(
              (decodeProfile) => decodeProfile.dataPointId === dataPoint.id
            )
          );
        } else {
          filteredDataPoints = allDataPoints.data;
        }

        if (selected) {
          currentDataPoint = allDataPoints.data.find(
            (dataPoint) => dataPoint.id === selected
          );

          const alreadyIncluded = filteredDataPoints.find(
            (dataPoint) => dataPoint.id === selected
          );

          if (!alreadyIncluded) {
            console.log('Adding filtered out datapoint', selected);
            currentDataPoint && filteredDataPoints.push(currentDataPoint);
          }
        }

        filteredDataPoints.sort((a, b) => a.name.localeCompare(b.name));

        setDataPoints(filteredDataPoints);
        setLoading(false);
      }
    }, [
      type,
      model,
      selected,
      filterUnused,
      filterUsed,
      allDataPoints.isLoading,
      allDataPoints.data,
      dataPointProfiles.isLoading,
      dataPointProfiles.data,
      filterForActionProfiles,
    ]);

    if (allDataPoints.isLoading || dataPointProfiles.isLoading || loading) {
      return (
        <div className="flex items-center justify-center">
          <LoadingWheel size="sm" />
        </div>
      );
    } else {
      const selectProps = { ...rest } as Omit<React.SelectHTMLAttributes<
        HTMLSelectElement
      >, 'color'>;

      // If parent didn't provide a `value` (uncontrolled), allow the
      // component to initialize to `selected` but remain user-editable by
      // using `defaultValue` instead of forcing `value`.
      if (selectProps.value === undefined && selected !== undefined) {
        selectProps.defaultValue = selected;
      }

      return (
        <Select
          disabled={!type || !model}
          ref={ref}
          label={label}
          {...selectProps}
        >
          <option value="" disabled hidden>
            Please Select
          </option>
          <option value={''}>None</option>
          {dataPoints?.map((dataPoint) => (
            <option key={dataPoint.id} value={dataPoint.id}>
              {dataPoint.name}{' '}
              {dataPoint.unit !== '' ? `(${dataPoint.unit})` : ''}
            </option>
          ))}
        </Select>
      );
    }
  }
);
